<template>

    <v-container fluid :class="dialogModel ? 'pa-0' : 'pa-3'" v-if="sessionActive">
        <v-card flat>
            <material-header v-if="!dialogModel"
                             :title="isEditing ? dataVariableName : 'NEW VARIABLE'"
                             :info="isEditing ? 'Editing variable' : 'Creating variable'">

                <template v-slot:buttons>

                    <v-btn v-if="isEditing"
                           :disabled="!hasPermission('update data variables')"
                           color="primary"
                           :loading="saving"
                           @click="save()">
                        Update
                    </v-btn>

                    <v-btn v-if="!isEditing"
                           :loading="saving"
                           :disabled="!hasPermission('create data variables')"
                           color="primary" @click="save()">
                        Create
                    </v-btn>

                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" fab small class="ml-4 elevation-2" color="red" dark @click="closeEdit">
                                <v-icon> mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Close editor</span>
                    </v-tooltip>

                </template>
            </material-header>

            <toolbar-page title="Edit variable" class="mb-2" v-if="dialogModel" disable-icon>
                <template slot="buttons">

                    <v-btn v-if="isEditing"
                           :disabled="!hasPermission('update data variables')"
                           color="primary"
                           :loading="saving"
                           @click="save()">
                        Update
                    </v-btn>

                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" @click="closeEdit">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Close</span>
                    </v-tooltip>
                </template>
            </toolbar-page>

            <v-card-text class="pt-0">

                <v-tabs v-model="activeTab"
                        dark
                        color="white"
                        slider-color="primary">

                    <v-tab key="settings" v-if="!dialogModel">Settings</v-tab>
                    <v-tab key="localizations">Question</v-tab>
                    <v-tab key="validators">Validators</v-tab>
                    <v-tab key="dynamicProfiling" v-if="!dialogModel">Dynamic profiling</v-tab>

                    <v-tab-item eager key="settings" v-if="!dialogModel">
                        <settings-tab
                            :data-variable="dataVariable"
                            :available-locales="availableLocales"
                            @change="variableChanged"
                        ></settings-tab>
                    </v-tab-item>

                    <v-tab-item eager key="localizations">
                        <question-tab
                            v-if="availableLocales && dataVariable"
                            :data-variable="dataVariable"
                            :available-locales="availableLocales"
                            :selected-locales="locales"
                            @change="variableChanged"
                        ></question-tab>
                    </v-tab-item>

                    <v-tab-item key="validators">
                        <validators-tab
                            :data-variable="dataVariable"
                            :available-locales="availableLocales"
                            @change="variableChanged"
                        ></validators-tab>
                    </v-tab-item>

                    <v-tab-item key="dynamicProfiling" v-if="!dialogModel">
                        <dynamic-profiling-tab
                            :data-variable="dataVariable"
                            :available-locales="availableLocales"
                            :selected-locales="locales"
                            @change="variableChanged"
                        ></dynamic-profiling-tab>
                    </v-tab-item>

                </v-tabs>

                <json v-if="false" :data="dataVariable"></json>
            </v-card-text>
        </v-card>

    </v-container>
</template>

<script>

import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import Json from 'vue-json-pretty';
import SettingsTab from './Tabs/SettingsTab';
import DynamicProfilingTab from './Tabs/DynamicProfilingTab';
import ValidatorsTab from "./Tabs/ValidatorsTab";
import QuestionTab from "./Tabs/QuestionTab";
import ToolbarPage from "../../../components/UI/ToolbarPage";
import Store from "../../DataVariables/store";
import {v4 as uuid} from 'uuid';

const vuexModule = 'DataVariables';

export default {

    mixins: [
        activeSessionChecker,
    ],

    components: {
        MaterialHeader,
        Json,
        SettingsTab,
        DynamicProfilingTab,
        ValidatorsTab,
        QuestionTab,
        ToolbarPage
    },

    props: {
        id: {
            type: Number | null,
            required: false,
            default: null,
        },
        type: {
            type: String | null,
            required: false,
        },
        locales: {
            type: Array | null,
            required: false,
        },
        dialogModel: {
            type: Boolean,
            required: false,
            default: false,
        }
    },

    data() {
        return {
            dataVariable: null,
            saving: false,
            activeTab: 0,
            validationErrorCount: 0,
        }
    },

    mounted() {
        this.loadDataVariables();
    },

    computed: {

        availableLocales() {
            return this.$store.state.PanelSettings.settings.locales;
        },

        dataVariableTypes() {
            return this.$store.state.dataVariableTypes;
        },

        isEditing() {
            return !!this.id;
        },

        hasOptions() {
            return this.dataVariable && (this.dataVariable.type === "radio" || this.dataVariable === "checkbox");
        },

        dataVariableName() {
            if (this.dataVariable) {
                return this.dataVariable.name.replace('_', ' ');
            }
        }
    },

    methods: {

        variableChanged(variable) {
            this.dataVariable = Object.assign({}, variable);
        },

        loadDataVariables() {
            this.$store.dispatch('getDataVariables')
                .then(() => {
                    // Do not load on create
                    if (this.id) {
                        this.findDataVariable();
                    } else {
                        // Default when creating new
                        this.createBlankVariable();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    Bus.$emit('server-error');
                });
        },

        findDataVariable() {
            let found = this.$store.state.dataVariables.find((item) => {
                return item.id === parseInt(this.id);
            });

            // Deep clone
            this.dataVariable = JSON.parse(JSON.stringify(found));
            this.addUniqueIdentifiers();
        },

        addUniqueIdentifiers() {
            if (this.dataVariable.options) {
                this.dataVariable.options.map(option => {
                    option.uuid = uuid();

                    option.locales.map(locale => {
                        locale.uuid = uuid();
                    });

                    return option;
                })
            }
        },

        createBlankVariable() {

            let variable = {
                id: null,
                type: this.type,
                name: '',
                user_editable: true,
                panelist_writable: true,
                user_localizable: true,
                dynamic_profiling_when_missing: false,
                dynamic_profiling_by_age: false,
                dynamic_profiling_priority: 5,
                community_api_writable: false,
                pi: false,
                locales: []
            };

            this.locales.forEach(locale => {
                variable.locales.push({
                    id: variable.locales.length + 1,
                    question_text: '',
                    locale_id: locale.id,
                });
            });

            if (this.type === 'checkbox' || this.type === 'radio') {
                variable.options = [
                    {
                        id: null,
                        order: 1,
                        name: '',
                        uuid: uuid(),
                        locales: []
                    }
                ];

                this.locales.forEach(locale => {
                    variable.options[0].locales.push({
                        id: null,
                        option_text: '',
                        locale_id: locale.id,
                        uuid: uuid()
                    });
                });
            }

            this.dataVariable = variable;
        },

        validationFails() {

            this.validationErrorCount = 0;

            if (this.dataVariable.name.length === 0) {
                this.showValidationError('Please enter data variable label',0);
            }

            if (this.hasOptions) {
                this.dataVariable.options.forEach(option => {
                    if (option.name.length === 0) {
                        this.showValidationError('Please fill in all the data variable option names!', 0);
                    }
                });
            }

            this.dataVariable.locales.forEach(locale => {
                if (locale.question_text.length === 0) {
                    this.showValidationError('Question text is missing for one or more locales!', 1);
                }
            })

            if (this.hasOptions) {
                this.dataVariable.options.forEach(option => {
                    option.locales.forEach(locale => {
                        if (locale.option_text.length === 0) {
                            this.showValidationError('Please fill in all the question option names!', 1);
                        }
                    })
                });
            }

            return this.validationErrorCount > 0;
        },

        showValidationError(message,tab) {

            if (this.validationErrorCount === 0) {

                Bus.$emit('flash-message', {
                    type: 'error',
                    message: message
                });

                this.activeTab = tab;
            }
            this.validationErrorCount++;
        },

        save() {

            if (this.validationFails()) return true;

            // Updating an existing data node
            if (this.isEditing) {

                this.saving = true;

                this.$store.dispatch('updateDataVariable', this.dataVariable)
                    .then(() => {
                        Bus.$emit('flash-message', {message: 'Data variable updated...'});

                        if (this.dialogModel) {
                            this.$emit('change');
                        } else {
                            this.$router.push({name: 'data-variables-list'});
                        }
                    })
                    .catch((error) => {

                        // Server side validation errors
                        if (error.response.status === 422) {
                            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                                Bus.$emit('flash-message', {message: value[0], type: 'error'});
                            });
                        } else {
                            Bus.$emit('flash-message', {message: error.response.data.message});
                        }
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            } else {

                this.saving = true;

                this.$store.dispatch('createDataVariable', this.dataVariable)
                    .then(() => {
                        Bus.$emit('flash-message', {message: 'Data variable created...'});
                        this.$router.push({name: 'data-variables-list'});
                    })
                    .catch((error) => {
                        // Server side validation errors
                        if (error.response.status === 422) {

                            Object.entries(error.response.data.errors).forEach(([key, value]) => {
                                Bus.$emit('flash-message', {message: value[0], type: 'error'});
                            });
                        } else {
                            Bus.$emit('flash-message', {message: error.response.data.message});
                        }
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            }
        },

        closeEdit() {
            if (this.dialogModel) {
                this.$emit('exit');
            } else {
                this.$router.push({name: 'data-variables-list'});
            }
        },
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    }
}
</script>
